import React, { useMemo, useState, useEffect } from "react";

import CommonInput from "../../../Components/Input";
import Select from "../../../Components/Select";
import ToggleSwitch from "../../../Components/ToggleSwitch";
import { isValidEmail } from "../../../Utils/Constants/RegexConstants";
import {
  ROLES,
  isAccountTeamRole,
  isRole,
} from "../../../Utils/Constants/RoleConstants";
import { toast } from "../../../Utils/Helper/toast";
import FormModal from "../../FormModal";
import "./style.scss";

interface AddUserModalProps {
  isOpen: boolean;
  toggle: () => void;
  organizations: { label: string; value: string }[];
  onSubmit: (data: any) => void;
  editData?: {
    organization: string;
    fullName: string;
    email: string;
    role: string;
    tenant_schema: string;
  } | null;
  isEditMode?: boolean;
  roleOptions: { label: string; value: string }[];
  accountTeamRoleOptions: { label: string; value: string }[];
  loading?: boolean;
}

/** Tenant DB role names not covered by isAccountTeamRole (ae, csm, …). */
const isAccountTeamUserRole = (role?: string | null): boolean => {
  if (!role) return false;
  if (isAccountTeamRole(role)) return true;
  const normalized = role.toLowerCase().trim();
  return (
    normalized === "account_executive" ||
    normalized === "customer_success_manager" ||
    normalized === "account_manager"
  );
};

const AddUserModal: React.FC<AddUserModalProps> = ({
  isOpen,
  toggle,
  organizations,
  onSubmit,
  editData,
  isEditMode = false,
  roleOptions,
  accountTeamRoleOptions,
  loading,
}) => {
  const [formData, setFormData] = useState({
    organization: "",
    fullName: "",
    email: "",
    role: "",
  });
  const [emailError, setEmailError] = useState("");
  const [wantToChangeRole, setWantToChangeRole] = useState(false);

  useEffect(() => {
    if (editData) {
      setFormData({
        organization: editData.organization,
        fullName: editData.fullName,
        email: editData.email,
        role: editData.role,
      });
      setEmailError("");
      setWantToChangeRole(false);
    } else {
      setFormData({
        organization: "",
        fullName: "",
        email: "",
        role: "",
      });
      setEmailError("");
      setWantToChangeRole(false);
    }
  }, [editData, isOpen]);

  const effectiveRole = useMemo(() => {
    if (isEditMode && !wantToChangeRole && editData) {
      return editData.role;
    }
    return formData.role;
  }, [isEditMode, wantToChangeRole, editData, formData.role]);

  const isExecutiveUser =
    isEditMode && !!editData && isRole(editData.role, ROLES.EXECUTIVE);

  const showAccountTeamFields = isAccountTeamUserRole(effectiveRole);

  const handleWantToChangeRole = (checked: boolean) => {
    if (checked && isExecutiveUser) {
      toast.info("Role cannot be changed for Executive users.");
      return;
    }
    setWantToChangeRole(checked);
  };

  const activeRoleOptions = showAccountTeamFields
    ? accountTeamRoleOptions
    : roleOptions;

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    if (isEditMode && name === "email") return;
    if (isEditMode && name === "organization") return;
    setFormData({ ...formData, [name]: value });

    if (name === "email") {
      if (value.trim() === "") {
        setEmailError("");
      } else if (!isValidEmail(value)) {
        setEmailError("Please enter a valid email address");
      } else {
        setEmailError("");
      }
    }
  };

  const handleSubmit = async () => {
    const basePayload =
      isEditMode && editData
        ? { ...formData, email: editData.email }
        : formData;
    const role =
      isEditMode && !wantToChangeRole && editData
        ? editData.role
        : formData.role;
    const payload = { ...basePayload, role };
    await onSubmit(payload);
  };

  const roleRequired = !isEditMode || wantToChangeRole;
  const isFormValid =
    formData.organization.trim() !== "" &&
    formData.fullName.trim() !== "" &&
    formData.email.trim() !== "" &&
    isValidEmail(formData.email) &&
    (!roleRequired || formData.role.trim() !== "") &&
    !emailError;

  return (
    <FormModal
      isOpen={isOpen}
      toggle={toggle}
      title={isEditMode ? "Edit User" : "Create New User"}
      onSubmit={handleSubmit}
      submitLabel={isEditMode ? "Update User" : "Create User"}
      isEditMode={isEditMode}
      loading={loading}
      disabled={!isFormValid}
    >
      <div className="add-user-modal">
        <Select
          label="Organization"
          name="organization"
          value={formData.organization}
          onChange={handleChange}
          options={organizations}
          placeholder="Select Organization"
          required
          disabled={isEditMode}
        />
        <CommonInput
          label="Full Name"
          name="fullName"
          value={formData.fullName}
          onChange={handleChange}
          placeholder="Enter full name"
          required
        />
        <CommonInput
          label="Email"
          name="email"
          type="email"
          value={formData.email}
          onChange={handleChange}
          placeholder="Enter email address"
          required
          error={emailError}
          disabled={isEditMode}
        />
        {isEditMode && (
          <ToggleSwitch
            className="add-user-modal__change-role"
            id="add-user-modal-change-role"
            label="Do you want to change the role?"
            checked={wantToChangeRole}
            onChange={handleWantToChangeRole}
          />
        )}
        {(wantToChangeRole || !isEditMode) && (
          <Select
            label="Role"
            name="role"
            value={formData.role}
            onChange={handleChange}
            options={activeRoleOptions}
            placeholder="Select Role"
            required={roleRequired}
          />
        )}
      </div>
    </FormModal>
  );
};

export default AddUserModal;
